import { ChangeDetectionStrategy, Component, computed, DestroyRef, effect, inject, input, model, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MultiSelect } from 'primeng/multiselect';
import { finalize } from 'rxjs';
import { TradeClassEntity } from '../trade-class.model';
import { TradeClassService } from '../trade-class.service';

@Component({
    selector: 'app-trade-class-multi-select',
    imports: [FormsModule, MultiSelect],
    templateUrl: './trade-class-multi-select.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TradeClassMultiSelectComponent {
    private destroyRef = inject(DestroyRef);
    private tradeClassService = inject(TradeClassService);

    selectedTradeClassKeys = model<number[]>([]);

    id = input<string>('tradeClassMultiSelect');
    placeHolder = input<string | undefined>('Select trade classes');

    subscriberKey = input.required<number | null>();

    invalid = input<boolean>(false);
    disabled = input<boolean>(false);

    options = signal<TradeClassEntity[]>([]);
    isLoading = signal<boolean>(false);
    loadingChange = output<boolean>();

    blur = output();

    isDisabled = computed(() => this.isLoading() || this.disabled());
    isInvalid = computed(() => this.invalid() && !this.isDisabled());
    
    constructor() {
        /* Fetch Options when Input Changes */
        effect(() => {
            const subscriberKey = this.subscriberKey();

            if (subscriberKey === null) {
                this.options.set([]);
                this.selectedTradeClassKeys.set([]);
            } else {
                this.loadOptions(subscriberKey);
            }
        });

        /* Loading Change */
        effect(() => {
            this.loadingChange.emit(this.isLoading());
        });
    }

    loadOptions(subscriberKey: number) {
        this.isLoading.set(true);
        this.options.set([]);

        this.tradeClassService
            .get(subscriberKey)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                finalize(() => {
                    this.isLoading.set(false);
                })
            )
            .subscribe((tradeClasses) => {
                const sorted = tradeClasses.sort((a, b) => a.tradeClassName.localeCompare(b.tradeClassName));
                this.options.set(sorted);
            });
    }

    onModelChange(tradeClassKeys: number[] | null) {
        this.selectedTradeClassKeys.set(tradeClassKeys ?? []);
    }
}
